'use client';

import { Button, Avatar } from '@heroui/react';
import { PeoplePopulated } from '@/util/types/serverTypeCollection/combinations';

export type JobContactSelectorMode = 'select' | 'add';

type BaseProps = {
  contacts: PeoplePopulated[];
  title?: string;
  emptyMessage?: string;
};

type SelectModeProps = BaseProps & {
  mode: 'select';
  selectedContactIds: Set<string>;
  onToggle: (contactId: string) => void;
};

type AddModeProps = BaseProps & {
  mode: 'add';
  onAddEmail: (email: string) => void;
  alreadyAddedEmails?: Set<string>;
};

export type JobContactSelectorProps = SelectModeProps | AddModeProps;

function getDisplayName(contact: PeoplePopulated): string {
  const { firstName, lastName, email } = contact._contactId;
  if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim();
  return email || 'Unknown';
}

function getEmail(contact: PeoplePopulated): string {
  return contact._contactId.email || '';
}

export default function JobContactSelector(props: JobContactSelectorProps) {
  const { contacts, title, emptyMessage } = props;
  const withEmail = contacts.filter((c) => getEmail(c));

  if (withEmail.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-600">{emptyMessage ?? 'No contacts with email available.'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {title && <p className='text-sm font-bold capitalize text-gray-700'>{title}</p>}
      <div className="space-y-2">
        {withEmail.map((person) => {
          const contactId = person._contactId._id;
          const email = getEmail(person);
          const displayName = getDisplayName(person);
          const roleName = (person._roleId as { name?: string })?.name ?? '';

          if (props.mode === 'select') {
            const selected = props.selectedContactIds.has(contactId);
            return (
              <div
                key={contactId}
                className="flex items-center justify-between rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <Avatar size="sm" name={displayName} className="text-white" />
                  <div>
                    <p className="text-sm font-medium">
                      {displayName}
                      {roleName && <span className="font-light text-gray-600"> – {roleName}</span>}
                    </p>
                    <p className="text-xs text-gray-500">{email}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={selected ? 'solid' : 'bordered'}
                  color={selected ? 'success' : 'default'}
                  onPress={() => props.onToggle(contactId)}
                >
                  {selected ? 'Selected' : 'Select'}
                </Button>
              </div>
            );
          }

          const added = props.alreadyAddedEmails?.has(email) ?? false;
          return (
            <div
              key={contactId}
              className="flex items-center justify-between rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <Avatar size="sm" name={displayName} className="text-white" />
                <div>
                  <p className="text-sm font-medium">
                    {displayName}
                    {roleName && <span className="font-light text-gray-600"> – {roleName}</span>}
                  </p>
                  <p className="text-xs text-gray-500">{email}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant={added ? 'solid' : 'bordered'}
                color={added ? 'default' : 'primary'}
                isDisabled={added}
                onPress={() => !added && props.onAddEmail(email)}
              >
                {added ? 'Added' : 'Add'}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
